import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { CrudHeader, CrudPageLayout } from 'Modules/Crud';
import { EditActions, MobileSaveBar } from 'Modules/UserAccount/Components/EditActions';
import { PasswordInput } from 'Views/Components/New/PasswordInput';
import { AccountDetailsTabHeader } from 'Views/Components/_HumanWritten/AccountDetails/AccountDetailsTabHeader';
import { UpdatePasswordLocationState } from 'Views/Pages/UpdatePasswordPage';
import { useUpdatePassword } from './useUpdatePassword';
import { PasswordConfiguration, PasswordOptions } from 'Global';
import { store } from 'Models/Store';
import { getPasswordOptionsForRole } from 'Validators/Functions/HumanWritten/Password';
import { camelCase } from '../../../../Util/StringUtils';

interface UpdatePasswordProps {
	userInfo?: UpdatePasswordLocationState;
}

/**
 * /update-password — reached only from the Password field on the account page, for the customer's
 * own account or (with userInfo in location state) for a customer a staff member is editing. It
 * shares the account page's chrome — identity header with Cancel/Update in it, one card below —
 * so the detour doesn't look like a different application.
 */
function UpdatePassword({ userInfo }: UpdatePasswordProps) {
	const { isMobile } = useDeviceDetect();

	const userRole = useMemo(() => {
		if (userInfo) {
			return userInfo.role;
		}
		return (store.userGroups.map(x => camelCase(x.name ?? '')) ?? [''])[0];
	}, [store, userInfo]);
	const options: PasswordOptions | PasswordConfiguration = useMemo(() => {
		return getPasswordOptionsForRole(store.passwordConfiguration, userRole);
	}, [store, userRole]);
	const { state, submitPassword, onUpdatePasswordClicked, onAfterChange } = useUpdatePassword(options, userInfo);

	const passwordRestrictions = useMemo(() => {
		const _passwordRestrictions: string[] = [];

		if (options.requiredLength && options.requiredLength > 0) {
			_passwordRestrictions.push(`${options.requiredLength} characters`);
		}
		if (options.requiredUniqueChars && options.requiredUniqueChars > 1) {
			_passwordRestrictions.push(`${options.requiredUniqueChars} unique characters`);
		}
		if (options.requireNonAlphanumeric) {
			_passwordRestrictions.push('At least one non alphanumeric character');
		}
		if (options.requireLowercase) {
			_passwordRestrictions.push('At least one lowercase character');
		}
		if (options.requireUppercase) {
			_passwordRestrictions.push('At least one uppercase character');
		}
		if (options.requireDigit) {
			_passwordRestrictions.push('At least one digit');
		}
		if (_passwordRestrictions.length === 0) {
			_passwordRestrictions.push('12 characters');
		}
		return _passwordRestrictions;
	}, [store.passwordConfiguration]);

	// Whose password this is: the staff flow carries the target customer in location state, the
	// self-service flow reads the logged-in user. Mirrors the account page's name + email header.
	const headerTitle = userInfo?.name ?? [store.firstName, store.lastName].filter(Boolean).join(' ');
	const headerSubtitle = userInfo?.email ?? store.userData?.email ?? '';

	const actions = (
		<EditActions
			saving={state.loading}
			onSave={submitPassword}
			onCancel={() => window.history.back()}
			saveLabel="Update password"
			savingLabel="Updating..."
			cancelId="cancel_register"
			saveId="confirm_reset_password"
		/>
	);

	return (
		<CrudPageLayout className="update-password-page">
			<CrudHeader
				title={headerTitle || 'Update password'}
				subtitle={headerSubtitle}
				actions={!isMobile ? actions : null}
			/>

			<div className="mx-auto mt-4 w-full max-w-[1000px] px-4 md:mt-6 md:px-8">
				<form onSubmit={onUpdatePasswordClicked}>
					<div className="account-page rounded-xl border border-gray-200 bg-white p-6 md:p-8">
						<div className="user-account-fields user-account-fields--edit">
							<AccountDetailsTabHeader headerText="Update password" />
							<div>
								<p className="!mb-1">Passwords must contain:</p>
								<ul className="!mb-0 list-disc pl-5 text-sm">
									{passwordRestrictions.map(x => (
										<li key={x}>{x}</li>
									))}
								</ul>
							</div>
							<PasswordInput
								id="new_password"
								className="new-password"
								label="New password"
								placeholder="New password"
								required
								value={state.password}
								error={state.errors.password}
								onChange={e => {
									const { value } = e.target;
									runInAction(() => {
										state.password = value;
									});
									onAfterChange('password');
								}}
							/>
							<PasswordInput
								id="confirm_password"
								className="confirm-password"
								label="Confirm password"
								placeholder="Confirm password"
								required
								value={state.confirmPassword}
								error={state.errors.confirmPassword}
								onChange={e => {
									const { value } = e.target;
									runInAction(() => {
										state.confirmPassword = value;
									});
									onAfterChange('confirmPassword');
								}}
							/>
						</div>
					</div>
					{/*
						Save lives in the header / mobile bar, outside the form. This keeps a submit
						control in the form so Enter still submits (two fields means there is no
						implicit submission without one).
					*/}
					<button type="submit" className="hidden" tabIndex={-1} aria-hidden="true">
						Update password
					</button>
				</form>
			</div>

			{isMobile && (
				<MobileSaveBar
					saving={state.loading}
					onSave={submitPassword}
					onCancel={() => window.history.back()}
					saveLabel="Update password"
					savingLabel="Updating..."
					cancelId="cancel_register"
					saveId="confirm_reset_password"
				/>
			)}
		</CrudPageLayout>
	);
}

export default observer(UpdatePassword);
